import { localizeHref } from '@/i18n/href'
import { pdfLinkProps } from '@/lib/pdf'
import { useI18n } from '@/i18n/I18nProvider'
import type { SectionOf } from './shared'

export default function CardsSection({ section }: { section: SectionOf<'cards'> }) {
  const { locale } = useI18n()
  // This section is the widest thing on the page and it should look it. The
  // heading and the standfirst run to `5xl`/`4xl` rather than the `3xl` the
  // argument sections use, so the headline uses the band instead of wrapping
  // inside its left half — and each column is a full-height flex column with
  // the link pushed to the bottom, so the three CTAs sit on one line even
  // when one card has a shorter promise. Without that the grid ends ragged
  // and the band reads as half empty on the right.
  //
  // `prominent` is the headingless variant: with no `heading` above them the
  // kickers have to do that job, so they go up to `lg` and full bold, and
  // every line under them steps up one size to match.
  const big = section.prominent === true
  return (
    <>
      {section.heading && (
        <h2 className="max-w-5xl text-3xl font-bold tracking-tight sm:text-4xl">
          {section.heading}
        </h2>
      )}
      {section.sub && (
        <p className="mt-4 max-w-4xl text-lg text-muted-foreground">{section.sub}</p>
      )}
      {/* A hairline between the columns, only where the cards end in a
          button. Asked for 2026-08-29, with a screenshot of the home page's
          three assessments: with nothing but whitespace between them, each
          Start button floated below a pile of bullets and read as belonging
          to no title in particular. The rule is the lightest thing that says
          "this column, from its title down to its button, is one card" —
          only on `lg`, where the three sit side by side, and never on the
          first column, whose left edge is the band's own margin. Guarded on
          three columns so the two-by-two layout does not draw a rule down
          the left edge of its second row.

          `ruledColumns` is the same rule asked for without the buttons: a
          band can end its cards in a text CTA and still want the columns
          separated. */}
      {(() => {
        const ruled =
          (section.buttonCtas || section.ruledColumns) && section.items.length === 3
        return (
      <div
        className={`grid gap-y-12 ${ruled ? 'gap-x-10' : 'gap-x-12'} ${
          // Four cards in a three-column grid leave one alone on a second
          // row with two empty cells beside it, which reads as a mistake.
          // Two-by-two instead — the tools hub went to four the day the ROI
          // calculator landed. Three stays three; nothing else changes.
          section.items.length === 4 ? 'lg:grid-cols-2' : 'lg:grid-cols-3'
        } ${big && !section.heading ? '' : 'mt-12'}`}
      >
        {section.items.map((c) => (
          <div
            key={c.title}
            className={`flex h-full flex-col${
              ruled ? ' lg:border-l lg:border-border lg:pl-10 lg:first:border-l-0 lg:first:pl-0' : ''
            }`}
          >
            {c.kicker && (
              <p
                className={`font-semibold uppercase text-primary ${
                  big
                    ? 'text-base font-bold tracking-[0.18em] lg:text-lg'
                    : 'text-sm tracking-[0.14em]'
                }${section.centeredKickers ? ' text-center' : ''}`}
              >
                {c.kicker}
              </p>
            )}
            <h3
              // `whitespace-pre-line` so a card title can choose its own
              // line break with a `\n` — a short title next to long ones
              // otherwise sits on one line while its neighbours wrap, and
              // the three cards start their promise at different heights.
              className={`whitespace-pre-line font-bold tracking-tight ${
                big ? 'text-3xl lg:text-4xl' : 'text-2xl'
              }${c.kicker ? (big ? ' mt-4' : ' mt-3') : ''}`}
            >
              {section.linkTitles ? (
                // Deliberately unstyled: the anchor inherits the heading's
                // colour and weight and adds no underline, so the title
                // looks identical and only the cursor gives it away. It is
                // `inline-block` so the hit area is the words, not the full
                // width of the column.
                <a
                  href={localizeHref(c.cta.href, locale)}
                  {...pdfLinkProps(c.cta.href)}
                  className="inline-block text-inherit no-underline hover:no-underline"
                >
                  {c.title}
                </a>
              ) : (
                c.title
              )}
            </h3>
            <p
              className={`leading-relaxed ${
                big ? 'mt-4 text-xl lg:text-2xl' : 'mt-3 text-lg lg:text-xl'
              }`}
            >
              {c.promise}
            </p>
            <ul
              className={`text-muted-foreground ${
                big ? 'mt-6 space-y-3 text-lg lg:text-xl' : 'mt-5 space-y-2 text-lg'
              }`}
            >
              {c.points.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
            {section.buttonCtas ? (
              // A real button, not a coloured line of text: same pill as
              // every other primary CTA on the site, at the tight size the
              // `cta` band uses inside a narrow column. `mt-auto` keeps the
              // three of them on one line however long the promise runs —
              // `pt-6` rather than `pt-8` pulls each one a little closer to
              // the points above it, since the column rule now carries the
              // separating job the extra space used to do badly.
              <div className={`mt-auto pt-6 ${section.ctaAlignLeft ? 'text-left' : 'text-center'}`}>
                <a
                  href={localizeHref(c.cta.href, locale)}
                  {...pdfLinkProps(c.cta.href)}
                  // Three buttons all reading "Explore" are the same link
                  // to a screen reader announcing them out of context, so
                  // each one names its own card.
                  aria-label={`${c.cta.label} ${c.title.replace(/\s+/g, ' ')}`}
                  className={`btn-primary ${
                    section.ctaArrow ? 'px-8 py-4 text-lg' : 'px-6 py-3.5 text-base'
                  }`}
                >
                  {c.cta.label}
                  {section.ctaArrow && ' →'}
                </a>
              </div>
            ) : (
              <a
                href={localizeHref(c.cta.href, locale)}
                {...pdfLinkProps(c.cta.href)}
                // Same reason as the button branch: several cards reading
                // "Start" are the same link to a screen reader announcing
                // them out of context, so each one names its own card.
                aria-label={`${c.cta.label} ${c.title.replace(/\s+/g, ' ')}`}
                className={`mt-auto self-start pt-8 font-bold text-primary hover:underline ${
                  big || section.ctaLarge ? 'text-2xl' : 'text-xl'
                }`}
              >
                {c.cta.label} →
              </a>
            )}
          </div>
        ))}
      </div>
        )
      })()}
    </>
  )
}
